import { ENVIRONMENT_URL, DATAVERSE_TABLES } from '../constants/dataverse';

const environmentUrl = ENVIRONMENT_URL;
/**
 * Backing PQI Service — completely independent.
 * Do NOT import or use existing PQI service or saveSectionData.
 * Table: cr3ea_prod_pqi_bakeries
 */

const API_VERSION = "9.2";

const STORAGE_TOKEN_KEYS = ["accessToken", "dvAccessToken", "token"];

function getBearerToken(): string | null {
  if (typeof sessionStorage === "undefined") return null;
  for (const key of STORAGE_TOKEN_KEYS) {
    const t = sessionStorage.getItem(key);
    if (t && t.trim()) return t.trim();
  }
  return null;
}

function getHeaders(token: string): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json; charset=utf-8",
    "OData-MaxVersion": "4.0",
    "OData-Version": "4.0",
    Authorization: `Bearer ${token}`,
  };
}

/**
 * Save a single record to cr3ea_prod_pqi_bakeries.
 * Uses Bearer token from sessionStorage (or passed token).
 */
export async function saveBackingPQIRecord(
  record: Record<string, any>,
  accessToken?: string | null
): Promise<{ success: boolean; data?: any; error?: string }> {
  const token = accessToken ?? getBearerToken();
  if (!token) {
    return { success: false, error: "No access token" };
  }
  const url = `${ENVIRONMENT_URL}/api/data/v${API_VERSION}/${DATAVERSE_TABLES.PQI_BAKERIES}`;
  const headers = {
    ...getHeaders(token),
    Prefer: "return=representation",
  };
  try {
    const res = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(record),
    });
    if (!res.ok) {
      const text = await res.text();
      return { success: false, error: `${res.status}: ${text}` };
    }
    const data = await res.json();
    return { success: true, data };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { success: false, error: msg };
  }
}

/**
 * Save multiple records to cr3ea_prod_pqi_bakeries.
 * Uses Bearer token from sessionStorage (or passed token).
 */
export async function saveMultipleBackingPQIRecords(
  records: Record<string, any>[],
  accessToken?: string | null
): Promise<{ success: boolean; results: any[]; errors: string[] }> {
  const token = accessToken ?? getBearerToken();
  if (!token) {
    return { success: false, results: [], errors: ["No access token"] };
  }
  const url = `${ENVIRONMENT_URL}/api/data/v${API_VERSION}/${DATAVERSE_TABLES.PQI_BAKERIES}`;
  const headers = {
    ...getHeaders(token),
    Prefer: "return=representation",
  };
  const results: any[] = [];
  const errors: string[] = [];
  for (let i = 0; i < records.length; i++) {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(records[i]),
      });
      if (!res.ok) {
        const text = await res.text();
        errors.push(`Record ${i + 1}: ${res.status} ${text}`);
        results.push(null);
      } else {
        results.push(await res.json());
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      errors.push(`Record ${i + 1}: ${msg}`);
      results.push(null);
    }
  }
  return {
    success: errors.length === 0,
    results,
    errors,
  };
}

/**
 * Upload attachment for a Backing PQI record (e.g. note or file).
 * Dataverse often uses annotation or a specific file column; adjust endpoint if your env uses a different attachment API.
 */
export async function uploadBackingPQIAttachment(
  recordId: string,
  file: File | Blob,
  fileName?: string,
  accessToken?: string | null
): Promise<{ success: boolean; data?: any; error?: string }> {
  const token = accessToken ?? getBearerToken();
  if (!token) {
    return { success: false, error: "No access token" };
  }
  const name = fileName || (file instanceof File ? file.name : "attachment");
  const formData = new FormData();
  formData.append("file", file, name);

  const url = `${ENVIRONMENT_URL}/api/data/v${API_VERSION}/${DATAVERSE_TABLES.PQI_BAKERIES}(${recordId})/cr3ea_attachment`;
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    "OData-MaxVersion": "4.0",
    "OData-Version": "4.0",
  };

  try {
    const res = await fetch(url, {
      method: "POST",
      headers,
      body: formData,
    });
    if (!res.ok) {
      const text = await res.text();
      return { success: false, error: `${res.status}: ${text}` };
    }
    const data = await res.json().catch(() => ({}));
    return { success: true, data };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { success: false, error: msg };
  }
}

// --- Legacy: keep for existing BackingPQI page that uses summary/cycle/saveSectionData-style ---
const API_VER = API_VERSION;

function formatPqiTitleDate(): string {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();
  return `${month}-${day}-${year}`;
}

function normalizeBakeriesRecord(item: any): any {
  if (!item) return item;
  const normalized = { ...item };
  if (item.cr3ea_status !== undefined) normalized.cr3ea_criteria = item.cr3ea_status;
  if (item.cr3ea_majordefect !== undefined) normalized.cr3ea_defectremarks = item.cr3ea_majordefect;
  if (item.cr3ea_itemno !== undefined) {
    normalized.cr3ea_evaluationtype = `Item ${item.cr3ea_itemno}`;
    normalized.cr3ea_packno = `Item ${item.cr3ea_itemno}`;
  }
  if (item.cr3ea_packaged !== undefined) normalized.cr3ea_pkd = item.cr3ea_packaged;
  return normalized;
}

export async function fetchBackingPQISummary(
  accessToken: string,
  qualityTourId: string
): Promise<any[] | null> {
  if (!accessToken || !qualityTourId) return null;
  const selectFields = [
    "cr3ea_category",
    "cr3ea_cycle",
    "cr3ea_status",
    "cr3ea_defectcategory",
    "cr3ea_netweight",
    "cr3ea_itemno",
    "cr3ea_defect",
    "cr3ea_majordefect",
  ].join(",");
  const apiUrl = `${environmentUrl}/api/data/v${API_VER}/${DATAVERSE_TABLES.PQI_BAKERIES}?$filter=cr3ea_qualitytourid eq '${qualityTourId}'&$select=${selectFields}`;
  const header = {
    Accept: "application/json",
    "Content-Type": "application/json; charset=utf-8",
    "OData-MaxVersion": "4.0",
    "OData-Version": "4.0",
    Authorization: `Bearer ${accessToken}`,
  };
  try {
    const response = await fetch(apiUrl, { method: "GET", headers: header });
    if (!response.ok) throw new Error(`Error fetching data: ${response.statusText}`);
    const responseData = await response.json();
    const value = responseData.value || [];
    if (value.length === 0) return null;
    return value.map(normalizeBakeriesRecord);
  } catch (error) {
    console.error("Backing PQI: Error loading summary data:", error);
    return null;
  }
}

export async function fetchBackingPQICycleDetails(
  accessToken: string,
  qualityTourId: string
): Promise<any[]> {
  if (!accessToken || !qualityTourId) return [];
  const apiUrl = `${environmentUrl}/api/data/v${API_VER}/${DATAVERSE_TABLES.PQI_BAKERIES}?$filter=cr3ea_qualitytourid eq '${qualityTourId}'`;
  const header = {
    Accept: "application/json",
    "Content-Type": "application/json; charset=utf-8",
    "OData-MaxVersion": "4.0",
    "OData-Version": "4.0",
    Authorization: `Bearer ${accessToken}`,
  };
  try {
    const response = await fetch(apiUrl, { headers: header });
    if (!response.ok) throw new Error("Error fetching cycle details");
    const responseData = await response.json();
    const value = responseData.value || [];
    return value.map(normalizeBakeriesRecord);
  } catch (error) {
    console.error("Backing PQI: Error loading cycle details:", error);
    return [];
  }
}

function toBakeriesRecord(raw: any, itemNumGlobal: number): any {
  return {
    cr3ea_qualitytourid: raw.cr3ea_qualitytourid ?? null,
    cr3ea_title: raw.cr3ea_title ?? "PQI_" + formatPqiTitleDate(),
    cr3ea_cycle: raw.cr3ea_cycle ?? null,
    cr3ea_shift:
      raw.cr3ea_shift ??
      (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("shiftValue") || "shift 1" : "shift 1"),
    cr3ea_observedby: raw.cr3ea_observedby ?? null,
    cr3ea_productname: raw.cr3ea_productname ?? null,
    cr3ea_batchno: raw.cr3ea_batchno ?? null,
    cr3ea_lineno: raw.cr3ea_lineno ?? null,
    cr3ea_executivename: raw.cr3ea_executivename ?? null,
    cr3ea_packaged: raw.cr3ea_packaged ?? raw.cr3ea_pkd ?? null,
    cr3ea_expiry: raw.cr3ea_expiry ?? raw.cr3ea_expirydate ?? null,
    cr3ea_standardweight: raw.cr3ea_standardweight ?? raw.cr3ea_standardntweight ?? null,
    cr3ea_netweight: raw.cr3ea_netweight ?? null,
    cr3ea_category: raw.cr3ea_category ?? null,
    cr3ea_itemno: raw.cr3ea_itemno ?? itemNumGlobal,
    cr3ea_status: raw.cr3ea_status ?? raw.cr3ea_criteria ?? null,
    cr3ea_defectcategory: raw.cr3ea_defectcategory ?? null,
    cr3ea_defect: raw.cr3ea_defect ?? null,
    cr3ea_majordefect: raw.cr3ea_majordefect ?? raw.cr3ea_defectremarks ?? null,
    cr3ea_remarks: raw.cr3ea_remarks ?? null,
  };
}

export async function saveBackingPQISectionData(
  accessToken: string,
  records: any[]
): Promise<any[]> {
  if (!accessToken || !records?.length) return [];
  const apiUrl = `${environmentUrl}/api/data/v${API_VER}/${DATAVERSE_TABLES.PQI_BAKERIES}`;
  const header = {
    Accept: "application/json",
    "Content-Type": "application/json; charset=utf-8",
    "OData-MaxVersion": "4.0",
    "OData-Version": "4.0",
    Prefer: "return=representation",
    Authorization: `Bearer ${accessToken}`,
  };
  const results: any[] = [];
  for (let i = 0; i < records.length; i++) {
    const record = toBakeriesRecord(records[i], i + 1);
    if (!record.cr3ea_cycle || !record.cr3ea_category || !record.cr3ea_qualitytourid) {
      results.push(null);
      continue;
    }
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: header,
        body: JSON.stringify(record),
      });
      if (!response.ok) {
        results.push(null);
        continue;
      }
      results.push(await response.json());
    } catch {
      results.push(null);
    }
  }
  return results;
}
